import { useEffect } from 'react';

import type { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';

/**
 * Triggers: `projectSiteRates` (the SRS rule matched for the current
 *           project/pickup pair).
 * Writes:   the Provider's `useVendorRateForDriver` state — OFF only. When
 *           the matched rule carries its own driver rate, the vendor→driver
 *           copy default must stop applying or submit would overwrite the
 *           rule's driver rate with the vendor's.
 * Guards:   fires only when the matched rule has a driver rate or driver
 *           rate type; a rule without one (or a clear to undefined) leaves
 *           the switch alone, exactly like legacy.
 * Legacy:   the legacy form disables the switch immediately after
 *           `setProjectSiteRates(rule)`, at both sites where that call
 *           happens; relocated here as a reaction to that state so the
 *           vendor→driver copy rule lives in one module (the only
 *           `projectSiteRates` writers are useSiteRateScheduleSync's
 *           fetch/match paths, so the reaction is behaviorally identical).
 * Test:     __tests__/OrderFormV3Context.test.tsx ("vendor→driver switch").
 */
export function useVendorRateToDriverCopy({
  projectSiteRates,
  setUseVendorRateForDriver,
}: {
  projectSiteRates: SiteRateSchedule | undefined;
  setUseVendorRateForDriver: (value: boolean) => void;
}): void {
  useEffect(() => {
    if (projectSiteRates?.driverRate || projectSiteRates?.driverRateType) {
      setUseVendorRateForDriver(false);
    }
    // The setter is read via closure, exactly as legacy reads its useState
    // setter at the two original sites.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectSiteRates]);
}
